import { StudioLogger } from '@studio/logger/StudioLogger';
import { SCREEN_PRESENTER } from '@shoflo/polymer';
import {
  createMeeting,
  increaseScreenShare
} from '@shoflo/studio-redux/actions';

import {
  updateSource,
  setUnassignedAttendeePresence,
  setIsInitializing,
  createSource
} from 'actions/Broadcast';
import {
  UNASSIGNED,
  isPendingKnock,
  isDirectorRequest
} from 'utils/broadcastSourceHelper';
import { ACCEPTED } from 'constants/KnockTypes';
import { withCurrentParams } from 'actions/utils';
import { SCREENSHARE } from 'constants/KnockTypes';
import { BROADCAST_APPROVE_ALL_KNOCKS } from 'actions/Types';
import { DIRECTOR_REQUEST_ANSWER } from 'store/eventConstants';
import { SCREEN_PRESENTER_LIMIT } from 'constants/SceneLayouts';
import { getApproveAllKnocksLogPayload } from 'utils/loggingUtils';
import { selectLastPresenterSource } from 'selectors/broadcastSourcesSelector';

const logger = new StudioLogger('knocks');

const DEFAULT_ORDER_NUMBER = 0;

/**
 * Handle source assignment for screenshare requests
 */
function handleSourceAssignment(dispatch, sourceId, sourceAttendeeId) {
  const value = `${sourceAttendeeId}#content`;
  dispatch(updateSource(sourceId, { value }));
  dispatch(setUnassignedAttendeePresence(value));
  dispatch(
    setIsInitializing(sourceId, {
      isInitializing: true,
      isAttendee: false
    })
  );
}

/**
 * Create a new screenshare source if one doesn't already exist
 */
function createScreenshareSourceIfNeeded(
  dispatch,
  sources,
  sourceAttendeeId,
  state
) {
  const value = `${sourceAttendeeId}#content`;
  const existingScreenShareSource = sources.find(
    source => source.value === value
  );

  if (existingScreenShareSource) {
    return;
  }

  let { order: lastPresenterOrder = DEFAULT_ORDER_NUMBER } =
    selectLastPresenterSource(state);
  const order = lastPresenterOrder + 1;
  let title = `${sourceAttendeeId}`;
  const attendeeSource = sources.find(
    source => source.value === sourceAttendeeId
  );
  if (attendeeSource && attendeeSource.title) {
    title = attendeeSource.title;
  }

  dispatch(
    createSource(
      {
        order,
        title,
        type: SCREEN_PRESENTER,
        value,
        description: ''
      },
      true
    )
  );
}

/**
 * Handle director request approval
 */
function handleDirectorRequest(
  dispatch,
  knock,
  sourceAttendeeId,
  sources,
  state,
  socket,
  knockPayload
) {
  const { sourceId, type } = knock;

  if (sourceId) {
    handleSourceAssignment(dispatch, sourceId, sourceAttendeeId);
  }

  if (type === SCREENSHARE && !sourceId) {
    createScreenshareSourceIfNeeded(dispatch, sources, sourceAttendeeId, state);
  }

  socket.emit(DIRECTOR_REQUEST_ANSWER, knockPayload);
}

/**
 * Process a single knock approval
 */
async function approveKnock(
  knock,
  dispatch,
  sources,
  state,
  socket,
  teamId,
  eventId,
  broadcastId,
  apiClient
) {
  const { name, presenterId, type, sourceId, attendeeId, sessionId, userId } =
    knock;

  const { broadcast, ...restResponse } =
    await apiClient.broadcastKnock.updateKnockRequest(
      teamId,
      eventId,
      broadcastId,
      knock._id,
      { name, presenterId, type, status: ACCEPTED }
    );

  if (type === SCREENSHARE) {
    dispatch(increaseScreenShare());
  }

  const sourceAttendeeId = attendeeId || getSourceAttendeeId(sources, sourceId);

  const knockPayload = {
    broadcastId: broadcast,
    userId,
    ...(isDirectorRequest(knock.type)
      ? { attendeeId: sourceAttendeeId }
      : { sourceId }),
    sessionId,
    ...restResponse
  };

  if (isDirectorRequest(knock.type)) {
    handleDirectorRequest(
      dispatch,
      knock,
      sourceAttendeeId,
      sources,
      state,
      socket,
      knockPayload
    );
  }

  return knockPayload;
}

export default () =>
  withCurrentParams(
    (dispatch, { teamId, eventId, broadcastId }, getState, socket) => {
      const state = getState();
      const {
        knocks,
        directorRequests,
        meeting,
        broadcastSources: { sources }
      } = state;

      const pendingKnocks = [...knocks, ...directorRequests].filter(
        isPendingKnock
      );

      const action = {
        type: BROADCAST_APPROVE_ALL_KNOCKS,
        request: async apiClient => {
          // we can only approve screen shares if
          // getShouldApproveScreenshares() returns true
          const shouldApproveScreenshares = getShouldApproveScreenshares(
            pendingKnocks,
            sources
          );

          /**
           * If the knock type is a screen share and we have decided not to approve
           * screen shares, this function filters out the knock preventing it from
           * being approved.
           *
           * @param {object} knock
           * @returns {boolean}
           */
          function filterScreenShares(knock) {
            return !(knock.type === SCREENSHARE && !shouldApproveScreenshares);
          }

          // Result is a list of approved knocks
          const updatedRequests = await Promise.all(
            pendingKnocks
              .filter(kn => filterScreenShares(kn))
              .map(kn =>
                approveKnock(
                  kn,
                  dispatch,
                  sources,
                  state,
                  socket,
                  teamId,
                  eventId,
                  broadcastId,
                  apiClient
                )
              )
          );

          const approvedKnockIds = getAllApprovedKnockIds(updatedRequests);

          const logPayload = getApproveAllKnocksLogPayload(
            approvedKnockIds,
            meeting
          );

          logger.info(logPayload.title, logPayload);

          if (!meeting.started) {
            dispatch(createMeeting());
          }

          /**
           * UpdatedRequests may contain both director requests and knocks
           * Properly handle in knocks reducer and director request reducer
           */
          return updatedRequests;
        }
      };

      return dispatch(action);
    }
  );

function getSourceAttendeeId(sources, sourceId) {
  const source = sources.find(source => source._id === sourceId);
  return source?.value;
}

function getAllApprovedKnockIds(knocks) {
  return knocks.map(knock => knock._id);
}
/**
 * Returns true if the no of available screen share slots matches the number of screen
 * share requests, else false.
 *
 * @param {array} sources
 * @param {array} pendingKnocks
 * @returns {array}
 */
function getShouldApproveScreenshares(pendingKnocks = [], sources = []) {
  const screenshareSourceCount = sources.filter(
    source => source.value !== UNASSIGNED && source.type === SCREEN_PRESENTER
  ).length;

  const screenshareRequestCount = pendingKnocks.filter(
    pendingKnock => pendingKnock.type === SCREENSHARE
  ).length;

  const availableSlots = SCREEN_PRESENTER_LIMIT - screenshareSourceCount;

  return availableSlots >= screenshareRequestCount;
}
